import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
  type SubmitEvent,
} from "react";
import { useFeatureNavigation } from "@softmax/web-features/navigation";
import { clampQuery, maxQueryChars } from "@softmax/web-features/site/records";
import { attachedPillElement } from "../pill-element";
import { usePillSearchGroups } from "./use-pill-search-groups";
import type { PillSearchScope } from "./pill-search-records";
export function usePillSearch({
  input,
  scope,
  member,
  all,
  onNavigate,
}: {
  readonly input: RefObject<HTMLInputElement | null>;
  readonly scope: PillSearchScope;
  readonly member: boolean;
  readonly all: ((query: string) => string) | null;
  readonly onNavigate: (href: string) => void;
}) {
  const router = useFeatureNavigation();
  const resultList = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  // An address keeps its highlight when an earlier group finishes loading.
  const [picked, setPicked] = useState<{ href: string; at: number } | null>(
    null,
  );
  const q = clampQuery(query);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(q), 200);
    return () => window.clearTimeout(timer);
  }, [q]);
  const ready = q === debounced;
  const groups = usePillSearchGroups({
    query: q,
    ready,
    scope,
    member,
  });
  const allHref = all && q !== "" ? all(q) : null;
  const options = groups.flatMap((group) => group.rows.map((row) => row.href));

  if (allHref) options.push(allHref);

  let active = -1;

  if (picked)
    active =
      options[picked.at] === picked.href
        ? picked.at
        : options.indexOf(picked.href);

  function setActive(at: number) {
    setPicked(options[at] === undefined ? null : { href: options[at], at });
  }

  function changeQuery(value: string) {
    setQuery([...value].slice(0, maxQueryChars).join(""));
    setPicked(null);
  }

  function moveSelection(direction: "ArrowDown" | "ArrowUp") {
    if (options.length === 0) return;

    const next = active + (direction === "ArrowDown" ? 1 : -1);
    let at = next;

    if (next < -1) at = options.length - 1;
    else if (next >= options.length) at = -1;

    if (at === -1) attachedPillElement(input).focus();
    else {
      const links =
        attachedPillElement(resultList).querySelectorAll<HTMLAnchorElement>(
          "[data-pill-result]",
        );
      const link = links[at];

      if (link === undefined)
        throw new Error("The selected pill result must be rendered.");

      link.focus();
    }

    setActive(at);
  }

  function onKeyDown(
    event: KeyboardEvent<HTMLInputElement | HTMLAnchorElement>,
  ) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();

      moveSelection(event.key);
      return;
    }

    if (event.key === "Escape" && query !== "") {
      event.preventDefault();
      event.stopPropagation();
      attachedPillElement(input).focus();
      changeQuery("");
      return;
    }

    if (
      event.currentTarget !== input.current &&
      event.key.length === 1 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey
    ) {
      event.preventDefault();
      attachedPillElement(input).focus();
      changeQuery(query + event.key);
    }
  }

  function onSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const target = active >= 0 ? options[active] : (allHref ?? options[0]);

    if (q !== "" && target) {
      onNavigate(target);
      router.push({ href: target });
    }
  }
  return {
    resultList,
    query,
    q,
    groups,
    ready,
    allHref,
    active,
    setActive,
    changeQuery,
    onKeyDown,
    onSubmit,
  };
}
